import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PusherHelper from '../services/pusher/PusherHelper';
import { systemChannel } from '../services/pusher/Channels';
import { useAuth } from './AuthProvider';

export const PusherConnectionState = {
  Initialized: 'initialized',
  Connecting: 'connecting',
  Connected: 'connected',
  Unavailable: 'unavailable',
  Failed: 'failed',
  Disconnected: 'disconnected',
};

export const PusherEventType = {
  ForceRefresh: 'force_refresh',
  OrderPaid: 'order.paid',
  OrderRefunded: 'order.refunded',
  OrderUpdated: 'order.updated',
  PrintOrder: 'order.print',
  OrderPickupTimeOptimized: 'order.pickup_time_optimized',
  OrderComplete: 'order.complete',
  RefundCreated: 'refund.created',
  OrdersPaused: 'orders.paused',
  OrdersUnpaused: 'orders.unpaused',
  TerminalSettingsUpdates: 'terminal_settings.updated',
};

export const PusherContext = createContext(null);

export function usePusher() {
  return useContext(PusherContext);
}

export default function PusherProvider({ children }) {
  const { isLoggedIn } = useAuth();

  const [pusherClient, setPusherClient] = useState(null);

  const pusherClientRef = useRef(pusherClient);
  pusherClientRef.current = pusherClient;

  useEffect(() => {
    isLoggedIn && connect();

    return () => disconnect();
  }, [isLoggedIn]);

  const connect = async () => {
    if (!PusherHelper.isConnected(pusherClientRef.current)) {
      const client = await PusherHelper.connect();
      setPusherClient(client);

      client.subscribe(systemChannel()).bind(PusherEventType.ForceRefresh, () => window.location.reload());

      return client.connection.bind('state_change', (states) => {
        const { previous, current } = states;

        if (previous === PusherConnectionState.Unavailable && current === PusherConnectionState.Connecting) {
          window.location.reload();
        }
      });
    }

    return pusherClientRef.current;
  };

  const disconnect = () => {
    pusherClientRef.current?.unbind_all();
    pusherClientRef.current?.disconnect();
    pusherClientRef.current?.unsubscribe(systemChannel());
    setPusherClient(null);
  };

  const unbind = (eventsToUnbind, channel = null) => {
    const events = typeof eventsToUnbind === 'string' ? [eventsToUnbind] : eventsToUnbind;

    if (channel) {
      events.forEach((event) => pusherClientRef.current?.channel(channel)?.unbind(event));
    }

    events.forEach((event) => pusherClientRef.current?.unbind(event));
  };

  const context = useMemo(
    () => ({
      client: pusherClientRef.current,
      isConnected: PusherHelper.isConnected(pusherClientRef.current),
      connect,
      disconnect,
      unbind,
      unsubscribe: (channel, eventsToUnbind) => {
        pusherClientRef.current?.unsubscribe(channel);
        unbind(eventsToUnbind, channel);
      },
    }),
    [pusherClientRef.current],
  );

  return <PusherContext.Provider value={context}>{children}</PusherContext.Provider>;
}
